<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import { $t } from '@/plugins/localization';
import { Form, FormType } from '@/helpers/Form';
import BpmnService, { BpmnEmitOptions, BpmnFormModel } from '@/modules/low-code/process/services/BpmnService';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';
import AddAttachments from '@/views/partials/components/attachments/AddAttachments.vue';
import GroupExecutionProgress from '@/modules/low-code/components/modals/process/GroupExecutionProgress.vue';

@Options({
    components: { AddAttachments, GroupExecutionProgress },
})
export default class BPMNAcceptModal extends Vue
{
    @Ref('modal') public modal: () => typeof IdeoModal;
    @Ref('exec') public exec: () => InstanceType<typeof GroupExecutionProgress>;

    public form: FormType<BpmnFormModel> = Form.create<BpmnFormModel>({
        comment: '',
        attachments: [],
    });

    public action = Form.create<BpmnEmitOptions>({
        id: '',
        licence: '',
        actionName: '',
        buttonId: '',
        displayMode: 'Modal',
        displaySize: 'Medium',
        title: $t('[[[Akceptuj]]]'),
        buttonName: $t('[[[Akceptuj]]]'),
        buttonVariant: 'success',
        buttonIcon: '',
        groupAction: false,
    });

    public isExecuting: boolean = false;
    public executionStateId: string | null = null;

    public async mounted(): Promise<void>
    {
        this.$events.$on(FrontActionModal.FlowModel, (action: BpmnEmitOptions) =>
        {
            this.action.withData(action);
            this.modal().show();
        });
    }

    public unmounted(): void
    {
        this.$events.$off(FrontActionModal.FlowModel);
        const exec = this.$refs?.exec as InstanceType<typeof GroupExecutionProgress> | undefined;
        exec?.stop();
    }

    private onExecCompleted(): void
    {
        this.isExecuting = false;
        this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));

        setTimeout(() =>
        {
            this.$emit('reload');
            this.$events.$emit('refetchActions');
        }, 500);
    }

    private onExecError(ex: any): void
    {
        this.isExecuting = false;
        this.handleException(ex, {
            400: (ex: any) => this.$alert.warning(ex.message),
        });
    }

    public async submit(): Promise<void>
    {
        try
        {
            this.form.wait();

            // Group action
            if (this.action.groupAction)
            {
                const endpoint = `${this.action.licence}/${this.action.buttonId}/bpmn-execute/group`;
                const data: any = this.form.formatData();

                data._publicIds = this.action.id.split(',');

                const response: any = await BpmnService.execute(endpoint, data);

                this.executionStateId = response;
                this.isExecuting = true;

                this.$nextTick(() => this.exec()?.start(response, 500));
            }
            else
            {
                const endpoint = `${this.action.licence}/${this.action.id}/${this.action.buttonId}/bpmn-execute`;

                await BpmnService.execute(endpoint, this.form.formatData());
                this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));
                this.modal().hide();

                setTimeout(() =>
                {
                    this.$emit('reload');
                    this.$events.$emit('refetchActions');
                    this.form.reset();
                }, 500);
            }
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]')),
                ],
            });
        }
        finally
        {
            if (!this.isExecuting)
            {
                this.form.continue();
            }
        }
    }
}
</script>

<template>
    <IdeoModal ref="modal" :title="action.title" centered @close="form.reset()" @hidden="form.reset()">
        <template #default>
            <div v-if="executionStateId">
                <GroupExecutionProgress ref="exec" :licence="action.licence" @completed="onExecCompleted" @error="onExecError" />
            </div>

            <IdeoFormGroup :label="$t('[[[Dodaj załączniki]]]')">
                <AddAttachments v-model="form.attachments" :container="'documents'" />
            </IdeoFormGroup>
            <IdeoFormGroup
                :label="$t('[[[Uwagi]]]')"
                :invalid-feedback="form.$errors.first('comment')"
                :state="form.$errors.state('comment')"
            >
                <IdeoFormTextarea v-model="form.comment" rows="6" @input="form.$errors.clear('comment')" />
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton
                :variant="action.buttonVariant"
                :disabled="!form.active() || isExecuting"
                @click="submit"
            >
                <i v-if="action.buttonIcon" class="me-2" :class="action.buttonIcon"></i>
                <span v-if="isExecuting">{{ $t('[[[Wykonywanie...]]]') }}</span>
                <span v-else>{{ action.buttonName }}</span>
            </IdeoButton>
            <IdeoButton variant="secondary" :disabled="isExecuting" @click="cancel()">
                {{ executionStateId ? $t('[[[Zamknij]]]') : $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
